// src/components/RequestFlow/RequestFlow.js

import React, { useState, useEffect, useContext, useRef, useCallback } from 'react';
import { ClientContext } from '../../context/ClientContext';
import { SelectedOfferProvider } from '../../context/SelectedOfferContext';
import { SuggestedDataProvider } from '../../context/SuggestedDataContext';
import DataEntryStep from './Steps/DataEntry/DataEntryStep';
import DocumentUploadStep from './Steps/DocumentUpload/DocumentUploadStep';
import PaymentStep from './Steps/Payment/PaymentStep';
import RequestSetupStep from './Steps/RequestSetupStep';
import OfferMockStep from './Steps/Offer/OfferMockStep';
import PreConfirmationStep from './Steps/PreConfirmationStep';
import ContractSignatureStep from './Steps/ContractSignatureStep';
import ResumeEngineProcessingStep from './Steps/Resume/ResumeEngineProcessingStep';
import ResumeBlockedStep from './Steps/Resume/ResumeBlockedStep';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCheck } from '@fortawesome/free-solid-svg-icons';
import { trackRequestStarted, trackRequestAbandoned, trackRequestStepEntered } from '../../analytics/events';
import { useAnalyticsContext } from '../../analytics/useAnalyticsContext';
import api from '../../services/api';
import { createComersRequest, fetchRequestRequirements, fetchRequestResumeContext, fetchRequestStatus, mapTargetEmploymentToFormData, normalizeProductRequirements, trackRequestOnboardingStep } from '../../services/comersRequestFlow';
import {
  getRequestRejectionDetail,
  getRequestStatusMessage,
  isRequestBlocked,
  showRequestBlockedAlert
} from '../../utils/requestStatusGuard';
import { Stepper } from '../ui';
import RequestFlowErrorBoundary from '../RequestFlowErrorBoundary';

import './RequestFlow.css';

const INITIAL_REQUEST_STEPS = [
  { id: 'RequestSetupStep', label: 'Inicio de solicitud', component: RequestSetupStep },
  { id: 'OfferMockStep', label: 'Oferta', component: OfferMockStep },
  { id: 'DocumentUploadStep', label: 'Carga de documentos', component: DocumentUploadStep },
  { id: 'PreConfirmationStep', label: 'Pre-Confirmación', component: PreConfirmationStep }
];

const APPROVED_REQUEST_STEPS = [
  { id: 'OfferMockStep', label: 'Confirmación de oferta', component: OfferMockStep },
  { id: 'DataEntryStep', label: 'Datos Personales', component: DataEntryStep },
  { id: 'PaymentStep', label: 'Medio de pago', component: PaymentStep },
  { id: 'ContractSignatureStep', label: 'Firma y Confirmación', component: ContractSignatureStep }
];

const STEP_TO_ONBOARDING_STEP = {
  RequestSetupStep: 'employment',
  DataEntryStep: 'employment',
  OfferMockStep: 'offer',
  DocumentUploadStep: 'document_upload',
  PaymentStep: 'card',
  PreConfirmationStep: 'success',
  ContractSignatureStep: 'success'
};

const RESUME_STEP_TO_COMPONENT_ID = {
  offer: 'OfferMockStep',
  employment: 'DataEntryStep',
  document_upload: 'PaymentStep',
  card: 'PaymentStep',
  signature: 'ContractSignatureStep',
  confirm_after_signature: 'ContractSignatureStep',
  done: 'ContractSignatureStep'
};

const INITIAL_RESUME_STEP_TO_COMPONENT_ID = {
  offer: 'OfferMockStep',
  document_upload: 'DocumentUploadStep',
  success: 'PreConfirmationStep',
  engine_processing: 'ResumeEngineProcessingStep',
  blocked: 'ResumeBlockedStep'
};

const RESUME_ONLY_STEPS = new Set(['engine_processing', 'blocked']);

const EMPLOYMENT_LINK_FAILURE_CODES = new Set([
  'employment_link_ambiguous',
  'employment_not_linked',
  'employment_applicant_mismatch',
  'employment_target_mismatch',
  'employment_not_found',
]);

const buildEmploymentLinkFailedMessage = (uid) =>
  `La solicitud fue creada, pero no pudo vincularse la información laboral. Código de solicitud: ${uid}. Contactá a soporte para continuar.`;

const shouldBlockCreateForEmploymentLinkFailure = (error) => {
  const errorCode = error?.response?.data?.error?.code;
  const orphanRequestUid = error?.response?.data?.requestStatus?.uid;

  return Boolean(orphanRequestUid)
    && typeof orphanRequestUid === 'string'
    && orphanRequestUid.trim() !== ''
    && EMPLOYMENT_LINK_FAILURE_CODES.has(errorCode);
};

const resolveOrphanRequestUid = (error) => {
  const orphanRequestUid = error?.response?.data?.requestStatus?.uid;

  if (typeof orphanRequestUid !== 'string' || orphanRequestUid.trim() === '') {
    return null;
  }

  return orphanRequestUid;
};

const resolveResumeStepIndex = (flowDefinition, resumeStep, fallbackIndex = 0) => {
  const componentId = resumeStep || 'offer';
  const stepIndex = flowDefinition.findIndex((step) => step.id === componentId);
  return stepIndex >= 0 ? stepIndex : fallbackIndex;
};

const normalizeSelectedOfferFromResume = (selectedOffer) => {
  const offerId = selectedOffer?.offerId || selectedOffer?.id;
  const amount = Number(selectedOffer?.amount);
  const monthlyPayment = Number(selectedOffer?.monthlyPayment ?? selectedOffer?.installmentAmount);
  const term = Number(selectedOffer?.term ?? selectedOffer?.installments);

  if (!offerId
    || !Number.isFinite(amount) || amount <= 0
    || !Number.isFinite(monthlyPayment) || monthlyPayment <= 0
    || !Number.isFinite(term) || term <= 0) {
    return null;
  }

  return {
    id: offerId,
    amount,
    originalAmount: amount,
    term,
    monthlyPayment,
    interestRate: Number(selectedOffer.interestRate ?? selectedOffer.taxes ?? 0),
    financed_amount: amount,
    firstDueDate: selectedOffer.firstDueDate || '',
    riskOfferVersion: selectedOffer.riskOfferVersion || null
  };
};

const normalizeOffersFromResume = (context = {}) => {
  const preOffers = context.preOffers || context.pre_offers || [];
  const finalOffers = context.finalOffers || context.final_offers || [];
  const requestOffers = preOffers.length > 0 ? preOffers : finalOffers;

  return {
    requestOffers,
    offerSelectionMode: context.offer_stage || (preOffers.length > 0 ? 'pre' : 'final')
  };
};

const RequestFlow = ({
  onClose,
  onViewRequests,
  onFatalError,
  onResumeExistingRequest,
  flowVariant = 'initial',
  initialRequestUid = null
}) => {
  const { clientData } = useContext(ClientContext);
  const { commerceId, branchId } = useAnalyticsContext();
  const demoModeEnabled = process.env.REACT_APP_REQUEST_FLOW_DEMO === 'true';
  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [steps, setSteps] = useState([]);
  const [stepLabels, setStepsLabels] = useState([]);
  const [requestId, setRequestId] = useState(initialRequestUid);
  const [lastStepStatus, setLastStepStatus] = useState('pending');
  const lastStepStatusRef = useRef('pending');
  const completedOnboardingStepsRef = useRef(new Set());
  const enteredOnboardingStepsRef = useRef(new Set());
  const createRequestInFlightRef = useRef(false);

  const [formData, setFormData] = useState({});
  const [isCreatingRequest, setIsCreatingRequest] = useState(false);
  const [productRequirements, setProductRequirements] = useState(() => normalizeProductRequirements());
  const [resumeContext, setResumeContext] = useState(null);
  const [resumeSelectedOffer, setResumeSelectedOffer] = useState(null);
  const [isLoadingResumeContext, setIsLoadingResumeContext] = useState(Boolean(initialRequestUid) && !demoModeEnabled);
  const [bootstrapError, setBootstrapError] = useState(null);
  const [createBlockedRequest, setCreateBlockedRequest] = useState(null);
  const [resumeOnlyStep, setResumeOnlyStep] = useState(null);
  const [isRefreshingResumeContext, setIsRefreshingResumeContext] = useState(false);
  const [flowBlocked, setFlowBlocked] = useState(false);
  const flowBlockedRef = useRef(false);
  const requestStartedTrackedRef = useRef(false);
  const enableSelfieVerification =
    flowVariant === 'approved' && process.env.REACT_APP_SELFIE_VERIFICATION_ENABLED === 'true';

  // Track request started when RequestFlow mounts (only once per flow instance)
  useEffect(() => {
    if (commerceId && clientData?.id && !requestStartedTrackedRef.current) {
      trackRequestStarted({
        commerceId,
        branchId,
        availableCredit: clientData.available_credit || 0,
        currency: 'ARS',
        customerDni: clientData?.dni
      });
      requestStartedTrackedRef.current = true;
    }
  }, [commerceId, branchId, clientData?.id, clientData?.dni]);

  // Track request abandoned when RequestFlow closes without completion
  // Note: This will fire on unmount, but we rely on lastStepStatusRef to determine
  // if the request was completed. If lastStepStatusRef.current is 'success', the request was completed.
  useEffect(() => {
    return () => {
      // Only track abandonment if the request wasn't completed
      // This cleanup runs when component unmounts (flow closed)
      if (commerceId && lastStepStatusRef.current !== 'success') {
        trackRequestAbandoned({
          commerceId,
          branchId,
          reason: 'flow_closed',
          customerDni: clientData?.dni
        });
      }
    };
  }, [commerceId, branchId]);

  const handleRequestStatus = useCallback((requestStatus) => {
    if (demoModeEnabled || flowBlockedRef.current || !isRequestBlocked(requestStatus)) {
      return;
    }

    flowBlockedRef.current = true;
    setFlowBlocked(true);

    showRequestBlockedAlert({
      message: getRequestStatusMessage(requestStatus),
      detail: getRequestRejectionDetail(requestStatus),
      onViewRequests,
      onFatalError
    });
  }, [demoModeEnabled, onFatalError, onViewRequests]);

  const applyResumeContext = useCallback((context, flowDefinition) => {
    const normalizedOffer = normalizeSelectedOfferFromResume(context.selected_offer);
    const resumeOffers = normalizeOffersFromResume(context);
    const isResumeOnlyStep = flowVariant === 'initial' && RESUME_ONLY_STEPS.has(context.resume_step);
    const markResumeTrackingInitialized = (stepIndex) => {
      flowDefinition.slice(0, stepIndex).forEach((step) => {
        const onboardingStep = STEP_TO_ONBOARDING_STEP[step.id];
        if (onboardingStep) {
          completedOnboardingStepsRef.current.add(onboardingStep);
        }
      });

      const currentOnboardingStep = STEP_TO_ONBOARDING_STEP[flowDefinition[stepIndex]?.id];
      if (currentOnboardingStep) {
        enteredOnboardingStepsRef.current.add(currentOnboardingStep);
      }
    };

    setResumeContext(context);
    setProductRequirements(normalizeProductRequirements(context.product_requirements || context.productRequirements));
    setResumeSelectedOffer(normalizedOffer);

    if (isResumeOnlyStep) {
      markResumeTrackingInitialized(0);
      setResumeOnlyStep(context.resume_step);
      setFormData((prevData) => ({
        ...prevData,
        resumeContext: context,
        ...(resumeOffers.requestOffers.length > 0 ? {
          requestOffers: resumeOffers.requestOffers,
          offerSelectionMode: resumeOffers.offerSelectionMode
        } : {}),
        ...(normalizedOffer ? { selectedPlan: normalizedOffer } : {}),
        ...mapTargetEmploymentToFormData(context.target_employment)
      }));
      return;
    }

    setResumeOnlyStep(null);
    const resumeMap = flowVariant === 'approved'
      ? RESUME_STEP_TO_COMPONENT_ID
      : INITIAL_RESUME_STEP_TO_COMPONENT_ID;
    const componentId = resumeMap[context.resume_step] || flowDefinition[0]?.id || 'OfferMockStep';
    const fallbackIndex = flowVariant === 'initial' && componentId !== 'RequestSetupStep' ? 1 : 0;
    const nextStepIndex = resolveResumeStepIndex(flowDefinition, componentId, fallbackIndex);

    markResumeTrackingInitialized(nextStepIndex);
    setCurrentStepIndex(nextStepIndex);

    if (context.post_signature_close?.confirmed) {
      setLastStepStatus('success');
      lastStepStatusRef.current = 'success';
    }

    setFormData((prevData) => ({
      ...prevData,
      resumeContext: context,
      ...(resumeOffers.requestOffers.length > 0 ? {
        requestOffers: resumeOffers.requestOffers,
        offerSelectionMode: resumeOffers.offerSelectionMode
      } : {}),
      ...(normalizedOffer ? { selectedPlan: normalizedOffer } : {}),
      ...mapTargetEmploymentToFormData(context.target_employment)
    }));
  }, [flowVariant]);

  const handleRefreshResumeContext = useCallback(async () => {
    if (!requestId || demoModeEnabled) {
      return null;
    }

    setIsRefreshingResumeContext(true);
    try {
      const context = await fetchRequestResumeContext(requestId);
      const flowDefinition = flowVariant === 'approved' ? APPROVED_REQUEST_STEPS : INITIAL_REQUEST_STEPS;
      applyResumeContext(context, flowDefinition);
      return context;
    } catch (error) {
      console.error('Error refreshing request resume context:', error);
      setBootstrapError('No se pudo preparar el retome de la solicitud.');
      return null;
    } finally {
      setIsRefreshingResumeContext(false);
    }
  }, [applyResumeContext, demoModeEnabled, flowVariant, requestId]);

  useEffect(() => {
    const flowDefinition = flowVariant === 'approved' ? APPROVED_REQUEST_STEPS : INITIAL_REQUEST_STEPS;
    setSteps(flowDefinition.map((step) => step.component));
    setStepsLabels(flowDefinition.map((step) => step.label));
    setRequestId(initialRequestUid);
    setResumeContext(null);
    setResumeSelectedOffer(null);
    setResumeOnlyStep(null);
    setBootstrapError(null);
    setFlowBlocked(false);
    flowBlockedRef.current = false;
    createRequestInFlightRef.current = false;
    setIsCreatingRequest(false);
    completedOnboardingStepsRef.current = new Set();
    enteredOnboardingStepsRef.current = new Set();
    setLastStepStatus('pending');
    lastStepStatusRef.current = 'pending';

    if (demoModeEnabled) {
      setCurrentStepIndex(0);
      setIsLoadingResumeContext(false);
      return undefined;
    }

    if (!initialRequestUid) {
      setCurrentStepIndex(0);
      setIsLoadingResumeContext(false);
      return undefined;
    }

    let cancelled = false;
    setIsLoadingResumeContext(true);

    const applyContext = (context) => {
      applyResumeContext(context, flowDefinition);
    };

    const handleBootstrapFailure = async () => {
      setBootstrapError('No se pudo preparar el retome de la solicitud.');

      try {
        const { requestStatus } = await fetchRequestStatus(initialRequestUid);
        if (!cancelled && isRequestBlocked(requestStatus)) {
          handleRequestStatus(requestStatus);
        }
      } catch (statusError) {
        console.error('Error fetching request status after resume failure:', statusError);
      }
    };

    fetchRequestResumeContext(initialRequestUid)
      .then((context) => {
        if (cancelled) {
          return;
        }

        applyContext(context);
      })
      .catch((error) => {
        console.error('Error fetching request resume context:', error);
        if (!cancelled) {
          handleBootstrapFailure();
        }
      })
      .finally(() => {
        if (!cancelled) {
          setIsLoadingResumeContext(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [demoModeEnabled, flowVariant, initialRequestUid, handleRequestStatus, applyResumeContext]);

  const getFlowSteps = () => (flowVariant === 'approved' ? APPROVED_REQUEST_STEPS : INITIAL_REQUEST_STEPS);

  const getOnboardingStepForIndex = (index) => {
    const stepId = getFlowSteps()[index]?.id;
    return STEP_TO_ONBOARDING_STEP[stepId] || null;
  };

  useEffect(() => {
    if (demoModeEnabled) {
      return undefined;
    }

    const interceptorId = api.interceptors.response.use(
      (response) => {
        handleRequestStatus(response.data?.requestStatus);
        return response;
      },
      (error) => {
        handleRequestStatus(error.response?.data?.requestStatus);
        return Promise.reject(error);
      }
    );

    return () => {
      api.interceptors.response.eject(interceptorId);
    };
  }, [demoModeEnabled, handleRequestStatus]);

  useEffect(() => {
    if (demoModeEnabled || !requestId || flowBlockedRef.current) {
      return undefined;
    }

    let cancelled = false;

    fetchRequestStatus(requestId)
      .then(({ requestStatus, productRequirements: requirements }) => {
        if (!cancelled) {
          if (requirements) {
            setProductRequirements(normalizeProductRequirements(requirements));
          }
          handleRequestStatus(requestStatus);
        }
      })
      .catch((error) => {
        console.error('Error fetching request status:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [currentStepIndex, requestId, demoModeEnabled, handleRequestStatus]);

  useEffect(() => {
    if (demoModeEnabled || !requestId || flowBlockedRef.current) {
      return undefined;
    }

    let cancelled = false;

    fetchRequestRequirements(requestId)
      .then((requirements) => {
        if (!cancelled && requirements) {
          setProductRequirements(normalizeProductRequirements(requirements));
        }
      })
      .catch((error) => {
        console.error('Error fetching request requirements:', error);
      });

    return () => {
      cancelled = true;
    };
  }, [requestId, demoModeEnabled]);

  const sendOnboardingTracking = async (step, event, metadata = {}) => {
    if (demoModeEnabled || !requestId || !step) {
      return;
    }

    try {
      const response = await trackRequestOnboardingStep({
        requestUid: requestId,
        step,
        event,
        metadata: {
          flowVariant,
          source: 'comers_request_flow',
          ...metadata
        }
      });

      const selectedBank = response?.data?.requestSetup?.bank;
      if (selectedBank?.code) {
        setFormData((prevData) => {
          const currentBankCode = prevData?.requestSetup?.bank;
          const currentSelectedBankId = prevData?.requestSetup?.selectedBank?.id;

          if (
            String(currentBankCode || '') === String(selectedBank.code)
            && String(currentSelectedBankId || '') === String(selectedBank.id || '')
          ) {
            return prevData;
          }

          return {
            ...prevData,
            requestSetup: {
              ...(prevData.requestSetup || {}),
              bank: String(selectedBank.code),
              selectedBank: {
                id: selectedBank.id,
                code: String(selectedBank.code),
                name: selectedBank.name || ''
              }
            }
          };
        });
      }
    } catch (error) {
      console.error('Error tracking onboarding step:', error);
    }
  };

  useEffect(() => {
    if (demoModeEnabled || !requestId || isLoadingResumeContext) {
      return;
    }

    for (let index = 0; index < currentStepIndex; index += 1) {
      const onboardingStep = getOnboardingStepForIndex(index);
      if (!onboardingStep || completedOnboardingStepsRef.current.has(onboardingStep)) {
        continue;
      }

      completedOnboardingStepsRef.current.add(onboardingStep);
      sendOnboardingTracking(onboardingStep, 'completed', {
        sourceStep: getFlowSteps()[index]?.id,
        sourceStepIndex: index
      });
    }

    const currentOnboardingStep = getOnboardingStepForIndex(currentStepIndex);
    if (!currentOnboardingStep || enteredOnboardingStepsRef.current.has(currentOnboardingStep)) {
      return;
    }

    enteredOnboardingStepsRef.current.add(currentOnboardingStep);
    sendOnboardingTracking(currentOnboardingStep, 'entered', {
      sourceStep: getFlowSteps()[currentStepIndex]?.id,
      sourceStepIndex: currentStepIndex
    });
  }, [currentStepIndex, requestId, demoModeEnabled, flowVariant, isLoadingResumeContext]);

  const handleNext = async (data, stepStatus = null) => {
    const currentStepId = getFlowSteps()[currentStepIndex]?.id;
    const currentOnboardingStep = STEP_TO_ONBOARDING_STEP[currentStepId];
    const updatedData = {
      ...formData,
      ...data
    };

    setFormData(updatedData);

    if (
      currentStepId === 'RequestSetupStep'
      && flowVariant === 'initial'
      && !demoModeEnabled
      && !requestId
    ) {
      if (createBlockedRequest?.uid) {
        setBootstrapError(buildEmploymentLinkFailedMessage(createBlockedRequest.uid));
        return;
      }

      if (createRequestInFlightRef.current) {
        return;
      }

      createRequestInFlightRef.current = true;
      setIsCreatingRequest(true);

      try {
        const createdRequest = await createComersRequest({
          clientData,
          requestSetup: updatedData.requestSetup || {},
          hiddenCustomerData: updatedData.hiddenCustomerData || null
        });

        if (isRequestBlocked(createdRequest.requestStatus)) {
          handleRequestStatus(createdRequest.requestStatus);
          return;
        }

        if (!createdRequest.uid) {
          throw new Error('La API no devolvió el identificador de la solicitud.');
        }

        setRequestId(createdRequest.uid);
        setFormData((prevData) => ({
          ...prevData,
          ...mapTargetEmploymentToFormData(createdRequest.employment)
        }));

        const evaluation = createdRequest.initial_evaluation || {};
        if (evaluation.status === 'approved') {
          setResumeOnlyStep(null);
        } else {
          setResumeContext({
            initial_evaluation: evaluation,
            resume_step: 'engine_processing'
          });
          setResumeOnlyStep('engine_processing');
          return;
        }
      } catch (error) {
        const apiError = error?.response?.data?.error;
        const activeRequest = error?.response?.data?.data;

        if (apiError?.code === 'REQUEST_ACTIVE_EXISTS' && activeRequest?.uid) {
          if (typeof onResumeExistingRequest === 'function') {
            onResumeExistingRequest({
              uid: activeRequest.uid,
              resumeFlowVariant: activeRequest.resume_flow_variant || 'initial'
            });
          }
          return;
        }

        if (shouldBlockCreateForEmploymentLinkFailure(error)) {
          const orphanRequestUid = resolveOrphanRequestUid(error);
          setCreateBlockedRequest({
            uid: orphanRequestUid,
            reason: 'employment_link_failed'
          });
          setBootstrapError(buildEmploymentLinkFailedMessage(orphanRequestUid));
          return;
        }

        console.error('Error creating Commerce request:', error);
        setBootstrapError(
          apiError?.message
          || error?.response?.data?.message
          || 'No se pudo iniciar la evaluación de la solicitud.'
        );
        return;
      } finally {
        createRequestInFlightRef.current = false;
        setIsCreatingRequest(false);
      }
    }

    if (!demoModeEnabled && requestId && currentOnboardingStep && !completedOnboardingStepsRef.current.has(currentOnboardingStep)) {
      completedOnboardingStepsRef.current.add(currentOnboardingStep);
      sendOnboardingTracking(currentOnboardingStep, 'completed', {
        sourceStep: currentStepId,
        sourceStepIndex: currentStepIndex,
        terminalStep: currentStepIndex === steps.length - 1,
        stepStatus: stepStatus || undefined
      });
    }

    if (currentStepIndex === steps.length - 1 && stepStatus) {
      setLastStepStatus(stepStatus);
      lastStepStatusRef.current = stepStatus;
    } else {
      setCurrentStepIndex((prevStep) => prevStep + 1);
    }
  };

  const handleBack = () => {
    setCurrentStepIndex((prevStep) => prevStep - 1);
  };

  // Track step entered
  useEffect(() => {
    if (commerceId && steps.length > 0 && currentStepIndex < steps.length) {
      const stepName = stepLabels[currentStepIndex] || `Step${currentStepIndex + 1}`;
      // Map step labels to step names
      const stepNameMap = {
        'Inicio de solicitud': 'RequestSetupStep',
        'Oferta': 'OfferMockStep',
        'Confirmación de oferta': 'OfferMockStep',
        'Datos Personales': 'DataEntryStep',
        'Carga de documentos': 'DocumentUploadStep',
        'Pre-Confirmación': 'PreConfirmationStep',
        'Medio de Pago': 'PaymentStep',
        'Medio de pago': 'PaymentStep',
        'Firma y Confirmación': 'ContractSignatureStep'
      };
      const mappedStepName = stepNameMap[stepName] || stepName;
      
      trackRequestStepEntered({
        commerceId,
        branchId,
        stepName: mappedStepName,
        customerDni: clientData?.dni
      });
    }
  }, [commerceId, branchId, currentStepIndex, steps.length, stepLabels, clientData?.dni]);

  const renderStep = () => {
    if (flowBlocked) {
      return null;
    }

    if (bootstrapError) {
      return (
        <div className="container py-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center p-4">
              <p className="text-danger mb-3">{bootstrapError}</p>
              <button type="button" className="btn btn-primary me-2" onClick={onFatalError}>
                Volver al perfil
              </button>
              {typeof onViewRequests === 'function' && (
                <button type="button" className="btn btn-outline-secondary" onClick={onViewRequests}>
                  Ver solicitudes
                </button>
              )}
            </div>
          </div>
        </div>
      );
    }

    if (isLoadingResumeContext) {
      return (
        <div className="container py-4">
          <div className="card border-0 shadow-sm">
            <div className="card-body text-center p-4">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Cargando...</span>
              </div>
              <p className="text-muted mt-3 mb-0">Preparando el retome de la solicitud...</p>
            </div>
          </div>
        </div>
      );
    }

    if (steps.length === 0) {
      return null;
    }

    if (resumeOnlyStep === 'engine_processing') {
      return (
        <ResumeEngineProcessingStep
          onRefreshResumeContext={handleRefreshResumeContext}
          isRefreshing={isRefreshingResumeContext}
          initialEvaluation={resumeContext?.initial_evaluation}
          onFatalError={onFatalError}
          onViewRequests={onViewRequests}
        />
      );
    }

    if (resumeOnlyStep === 'blocked') {
      return (
        <ResumeBlockedStep
          blockingReason={resumeContext?.blocking_reason}
          onFatalError={onFatalError}
          onViewRequests={onViewRequests}
        />
      );
    }

    const CurrentStepComponent = steps[currentStepIndex];

    if (!CurrentStepComponent) {
      console.error(`Componente para el paso index ${currentStepIndex} no está definido.`);
      return <div>Error al cargar el paso. Por favor, intenta nuevamente.</div>;
    }

    return (
      <CurrentStepComponent
        clientData={clientData}
        requestId={requestId}
        setRequestId={setRequestId}
        onNext={handleNext}
        onBack={handleBack}
        onClose={onClose}
        onViewRequests={onViewRequests}
        onFatalError={onFatalError}
        onResumeExistingRequest={onResumeExistingRequest}
        onRefreshResumeContext={handleRefreshResumeContext}
        formData={formData}
        productRequirements={productRequirements}
        resumeContext={resumeContext}
        demoMode={demoModeEnabled}
        flowVariant={flowVariant}
        enableSelfieVerification={enableSelfieVerification}
        isLoading={currentStepIndex === 0 && (isCreatingRequest || Boolean(createBlockedRequest?.uid))}
      />
    );
  };

  const stepperSteps = steps.map((_, index) => {
    const isCompleted = index < currentStepIndex || (index === steps.length - 1 && lastStepStatus === 'success');
    const isActive = index === currentStepIndex && !isCompleted;

    return {
      id: getFlowSteps()[index]?.id || `step-${index}`,
      label: stepLabels[index],
      number: false,
      state: isCompleted ? 'completed' : isActive ? 'active' : undefined,
      icon: isCompleted ? <FontAwesomeIcon icon={faCheck} /> : <span>{index + 1}</span>
    };
  });

  const calculateProgress = () => {
    const totalSteps = steps.length;
    if (totalSteps <= 1) return 0;

    if (currentStepIndex === totalSteps - 1 && lastStepStatus === 'success') {
      return 100;
    }

    return (currentStepIndex / (totalSteps - 1)) * 100;
  };

  return (
    <SelectedOfferProvider initialSelectedOffer={resumeSelectedOffer}>
      <SuggestedDataProvider>
        <div className="request-flow rg-request-flow">

          {/* Stepper con barra de progreso - solo visible si no está confirmado */}
          {!(currentStepIndex === steps.length - 1 && lastStepStatus === 'success') && !resumeOnlyStep && (
            <div className="stepper-container rg-request-flow__stepper">
              <Stepper
                steps={stepperSteps}
                className="stepper-wrapper rg-stepper--compact rg-stepper--request-flow"
                ariaLabel="Pasos de la solicitud"
              />
              <div className="progress-bar-wrapper rg-request-flow__progress">
                <div
                  className="progress-bar"
                  style={{ width: `${calculateProgress()}%` }}
                />
              </div>
            </div>
          )}

          {/* Renderizar el componente del paso actual */}
          <RequestFlowErrorBoundary key={currentStepIndex}>
            {renderStep()}
          </RequestFlowErrorBoundary>
        </div>
      </SuggestedDataProvider>
    </SelectedOfferProvider>
  );
};

export default RequestFlow;
